import { Box, Grid, Rating, Skeleton, Stack, Typography } from '@mui/material';
import { renderFingerprintImage } from 'Components/Applicants/ApplicantHelpers';
import { SkillMap } from 'Components/Applicants/ApplicantsConstants';
import { CandidateEvalImgCntnr, FingerprintImageContainer } from 'Components/Applicants/ApplicantsList/ApplicantsList.styled';
import { IApplicantSummary } from 'Components/Applicants/ApplicantsModel';
import { useAppDispatch } from 'helpers/hooks';
import moment from 'moment';
import { useEffect } from 'react';
import { ApiState, ISummaryComponentBase } from 'shared/SharedModels';
import { ShAlert } from 'shared/SharedStyles/ShFeedback';
import { DefaultAPIErrorMsg } from 'shared/constants';
import { getFingerprintImage } from 'store/slices/employer/applicants/applicants-list-slice';

export const ApplicantSummary = ({ summary, applicantId }: (ISummaryComponentBase<IApplicantSummary> & { applicantId: number })) => {
    const dispatch = useAppDispatch();

    // Load fingerprint image once summary is loaded.
    useEffect(() => {
        dispatch(getFingerprintImage({ applicantId: applicantId }));
    }, [applicantId, dispatch]);

    // Append the svg into parent element once its loaded. 
    useEffect(() => {
        if (summary.getFingerprintImageStatus === 'success') {
            if (summary.baseballCardUrl) {
                renderFingerprintImage(summary.baseballCardUrl, applicantId + 'fingerprint_svg_parent');
            }
        }
    }, [applicantId, summary.baseballCardUrl, summary.getFingerprintImageStatus]);

    const showFailureAlerts = (apiState: ApiState, msg: string) => {
        switch (apiState) {
            case 'failed':
                return (<>
                    <Box marginBottom={2} width='100%'>
                        <ShAlert severity="error">{msg ?? DefaultAPIErrorMsg}</ShAlert>
                    </Box>
                </>);
            default:
                break;
        }
    };

    return (<>
        <Grid container columnSpacing={{ xs: 0, sm: 2, md: 2.5, lg: 3, xl: 3 }} padding={1}>
            <CandidateEvalImgCntnr item xs={12} sm={12} md={6} lg={5} xl={4}>
                {(summary?.getFingerprintImageStatus && summary?.getFingerprintImageResponse) &&
                    showFailureAlerts(summary?.getFingerprintImageStatus, summary?.getFingerprintImageResponse)}
                {summary?.getFingerprintImageStatus === 'pending' ?
                    <Skeleton width='100%' height='100%' animation='wave' /> :
                    <>
                        {/* /Placeholder/Parent block to which the fingerprint SVG will be appended. */}
                        <FingerprintImageContainer id={applicantId + 'fingerprint_svg_parent'}></FingerprintImageContainer>
                    </>}
            </CandidateEvalImgCntnr>
            <Grid item xs={12} sm={12} md={6} lg={7} xl={8}>
                <Stack height='100%' justifyContent='center' rowGap={1}>
                    <Stack>
                        <Typography variant='subtitle2' fontWeight='bold' gutterBottom>Education & Qualifications</Typography>
                        {summary?.educationQualificationRequirements?.map((eq, indx) =>
                            (<Typography key={indx} variant='body2'>{eq.name}</Typography>))}
                    </Stack>
                    <Stack>
                        <Typography variant='subtitle2' fontWeight='bold' gutterBottom>Skills</Typography>
                        {summary?.skillRequirements?.map((skl, indx) =>
                        (<Stack flexDirection='row' key={indx}><Rating id={applicantId + ''} size='small' value={SkillMap[skl.skillRating] ?? 0} readOnly />
                            <Typography variant='body2'>{skl.name}</Typography></Stack>))}
                    </Stack>
                    {summary?.commentSummaries?.map(cmnt => (
                        <Stack key={cmnt.commentId} justifyContent='center'>
                            <Typography variant='subtitle2' fontWeight='bold'
                                gutterBottom width='fit-content'>Comments</Typography>
                            <Stack flexDirection='row' alignItems='center' columnGap={1} marginBottom={0.5}>
                                <Typography variant='body2' whiteSpace='nowrap'>Commented by :</Typography>
                                <Typography variant='body2' fontWeight='bold'>{cmnt.commentOwner}</Typography>
                            </Stack>
                            <Stack flexDirection='row' alignItems='center' columnGap={1} marginBottom={0.5}>
                                <Typography variant='body2' whiteSpace='nowrap'>Comment :</Typography>
                                <Typography variant='body2'>{cmnt.comment}</Typography>
                            </Stack>
                            <Stack flexDirection='row' alignItems='center' columnGap={1} marginBottom={0.5}>
                                <Typography variant='body2' whiteSpace='nowrap'>Commented on:</Typography>
                                <Typography variant='body2'>{moment(cmnt.commentDate).format("MMM DD, YYYY")}</Typography>
                            </Stack>
                        </Stack>
                    ))}
                </Stack>
            </Grid>
        </Grid>
    </>);
};